import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AccountKind, RoleDto, UserDetailDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'

const ACCOUNT_KIND_OPTIONS = [
  { value: AccountKind.User, label: 'User' },
  { value: AccountKind.Admin, label: 'Admin' },
  { value: AccountKind.Customer, label: 'Customer' },
]

const adminKindRequiresRole = (kind: AccountKind) => kind === AccountKind.Admin

interface AccountAccessModalProps extends BaseModalProps {
  publicId: string
  kind: AccountKind
  rolePublicId: string
  onSaved: (user: UserDetailDto) => void
}

const AccountAccessModal = ({
  isOpen,
  onClose,
  publicId,
  kind,
  rolePublicId,
  onSaved,
}: AccountAccessModalProps) => {
  const [draftKind, setDraftKind] = useState<AccountKind>(kind)
  const [draftRolePublicId, setDraftRolePublicId] = useState(rolePublicId)
  const [availableRoles, setAvailableRoles] = useState<RoleDto[]>([])
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setDraftKind(kind)
    setDraftRolePublicId(rolePublicId)
    setError(null)
    setIsSaving(false)
  }, [isOpen, kind, rolePublicId])

  useEffect(() => {
    if (!isOpen) return

    const fetchRoles = async () => {
      setIsLoadingRoles(true)
      try {
        const roles = await adminService.getRoles()
        setAvailableRoles(roles)
      } catch (err) {
        console.log('Failed to fetch roles', err)
        setError('Failed to load roles.')
      } finally {
        setIsLoadingRoles(false)
      }
    }

    fetchRoles()
  }, [isOpen])

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const canSubmit =
    draftKind === AccountKind.User ||
    !adminKindRequiresRole(draftKind) ||
    draftRolePublicId.length > 0

  const handleSave = async () => {
    if (!canSubmit) {
      setError('Admin accounts require a role.')
      return
    }

    try {
      setIsSaving(true)
      setError(null)
      const updated = await adminService.updateAccountKind(publicId, {
        kind: draftKind,
        rolePublicId:
          draftKind === AccountKind.User ? undefined : draftRolePublicId || undefined,
      })
      onSaved(updated)
      onClose()
    } catch (err) {
      console.log('Failed to update account access', err)
      setError('Failed to update account kind or role.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup account-access-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>Account access</h2>
        <p style={{ marginTop: 0, opacity: 0.85, fontSize: '0.95rem' }}>
          Set the portal type for this account and, when needed, assign a single RBAC role.
        </p>

        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <div className="form-group">
            <label>Account kind</label>
            <select
              value={draftKind}
              onChange={e => setDraftKind(e.target.value as AccountKind)}
              disabled={isSaving || isLoadingRoles}
            >
              {ACCOUNT_KIND_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Role</label>
            <select
              value={draftRolePublicId}
              onChange={e => setDraftRolePublicId(e.target.value)}
              disabled={isSaving || isLoadingRoles || draftKind === AccountKind.User}
            >
              <option value="">
                {draftKind === AccountKind.User
                  ? 'Not applicable'
                  : adminKindRequiresRole(draftKind)
                    ? 'Select a role…'
                    : 'No role'}
              </option>
              {availableRoles.map(role => (
                <option key={role.publicId} value={role.publicId}>
                  {role.name}
                </option>
              ))}
            </select>
            {draftKind === AccountKind.Admin && !draftRolePublicId && (
              <p style={{ margin: '6px 0 0', fontSize: '0.85rem', opacity: 0.75 }}>
                Admin accounts need a role before they can use the admin panel.
              </p>
            )}
          </div>
        </form>

        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}

        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="styled-button"
            onClick={handleSave}
            disabled={isSaving || isLoadingRoles || !canSubmit}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default AccountAccessModal
